import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "../../lib/api";
import ImageUpload from "../../components/ImageUpload";
import RichTextEditor from "../../components/RichTextEditor";

const FIELDS = [
  { key: "hero_tagline", label: "Hero tagline (overline)" },
  { key: "hero_title_line1", label: "Hero — title line 1" },
  { key: "hero_accent_word", label: "Hero — accent word (coloured)" },
  { key: "hero_title_line2", label: "Hero — title line 2" },
  { key: "hero_subtitle", label: "Hero subtitle", multiline: true },
  { key: "hero_cta_primary", label: "Hero CTA (primary button)" },
  { key: "hero_cta_secondary", label: "Hero CTA (secondary button)" },
  { key: "hero_image_url", label: "Hero image", type: "image" },
  { key: "hero_badge_number", label: "Hero badge — number" },
  { key: "hero_badge_title", label: "Hero badge — title" },
  { key: "hero_badge_subtitle", label: "Hero badge — subtitle" },

  { key: "boxes_overline", label: "Preset Boxes overline" },
  { key: "boxes_heading", label: "Preset Boxes heading" },

  { key: "flavours_overline", label: "Flavours overline" },
  { key: "flavours_heading", label: "Flavours heading" },

  { key: "delivery_overline", label: "Delivery overline" },
  { key: "delivery_heading_part1", label: "Delivery heading — part 1" },
  { key: "delivery_heading_accent", label: "Delivery heading — accent (coloured)" },
  { key: "delivery_subtext", label: "Delivery subtext", multiline: true },
  { key: "delivery_image_url", label: "Delivery image", type: "image" },
];

export default function AdminLanding() {
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => setData((await api.get("/landing")).data);
  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/landing", data);
      toast.success("Landing page updated — visitors will see changes immediately.");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to save");
    } finally { setSaving(false); }
  };

  if (!data) return <div className="text-sm text-zinc-500">Loading…</div>;

  return (
    <div data-testid="admin-landing">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <div className="overline text-zinc-500">Website</div>
          <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight">Landing page content</h1>
          <p className="text-sm text-zinc-500 mt-1">
            Use the formatting toolbar above each field for <b>bold</b>, <i>italic</i>, size and colour. Independent from product data.
          </p>
        </div>
        <button onClick={save} disabled={saving} className="btn-primary disabled:opacity-50" data-testid="save-landing-btn">
          {saving ? "Saving…" : "Save changes"}
        </button>
      </div>

      <div className="rounded-xl border border-zinc-200 bg-white p-5 sm:p-6 space-y-5">
        {FIELDS.map((f) => (
          <div key={f.key} className="grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-4 pb-5 border-b border-zinc-100 last:border-0 last:pb-0">
            <label className="text-sm font-semibold text-zinc-700">{f.label}</label>
            <div className="md:col-span-2 min-w-0">
              {f.type === "image" ? (
                <ImageUpload value={data[f.key] || ""} onChange={(url) => setData({ ...data, [f.key]: url })} testid={`landing-${f.key}`} />
              ) : (
                <RichTextEditor
                  value={data[f.key] || ""}
                  onChange={(html) => setData({ ...data, [f.key]: html })}
                  singleLine={!f.multiline}
                  testid={`landing-${f.key}`}
                />
              )}
            </div>
          </div>
        ))}
      </div>

      <div className="mt-6 flex justify-end">
        <button onClick={save} disabled={saving} className="btn-primary disabled:opacity-50">
          {saving ? "Saving…" : "Save changes"}
        </button>
      </div>
    </div>
  );
}
